import { FMSInterface, FMSType } from "@swivl-mono/types";
import { Button, ButtonColor, Input } from "@swivl-mono/ui";
import { ComponentProps, useState } from "react";
import { BiEdit } from "react-icons/bi";

import { isValidURL } from "../../../../../Models/Variables/LocationVars.Model";

interface InputProps extends ComponentProps<typeof Input> {}

function URLInput({ ...props }: InputProps) {
  const { label, name, value, onChange } = props;
  const hasError = typeof value === "string" && value.trim() !== "" && !isValidURL(value);

  return (
    <Input
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      error={hasError ? "Please enter a valid URL (e.g. https://example.com)" : undefined}
      {...props}
    />
  );
}

interface SecretInputProps extends ComponentProps<typeof Input> {
  isSet: boolean;
}

function SecretInput({ isSet, ...props }: SecretInputProps) {
  const { label, name, value, onChange } = props;
  return (
    <Input label={label} name={name} type="password" value={value} placeholder={isSet ? "Leave blank to keep current" : ""} onChange={onChange} />
  );
}

function ReadOnlyField({ label, value, isSecret }: { label: string; value: string | boolean; isSecret?: boolean }) {
  let display: string;
  if (isSecret) {
    display = value ? "••••••••" : "Unset";
  } else {
    display = (value as string) || "Unset";
  }
  return (
    <div className="flex items-baseline py-2">
      <label className="min-w-[140px] text-sm font-medium text-black">{label}</label>
      <div className="flex-1 text-sm font-semibold break-all text-black">{display}</div>
    </div>
  );
}

interface Props {
  fms: FMSInterface;
  onSave(updates: { token?: any; data?: any }): void;
  onRemove(): void;
}

export const FMSAPIColumn = (props: Props) => {
  const { fms, onSave, onRemove } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [draftToken, setDraftToken] = useState<any>({});
  const [draftData, setDraftData] = useState<any>({});

  const token = fms.token || {};
  const data = isEditing ? { ...(fms.data || {}), ...draftData } : fms.data || {};

  const onTokenChange = (e: any) => {
    setDraftToken((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onDataChange = (e: any) => {
    setDraftData((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const URL_TOKEN_FIELDS = ["base_url", "url", "baseUrl", "serviceUrl"];

  const mergedToken = { ...token, ...draftToken };
  const hasInvalidURL = URL_TOKEN_FIELDS.some((field) => {
    const value = mergedToken[field];
    return typeof value === "string" && value !== "" && !isValidURL(value);
  });

  const handleDone = () => {
    if (hasInvalidURL) {
      return;
    }

    // Build the token update: start with all fields from original token (including
    // boolean flags for secrets so local state preserves "is set" indicators),
    // then overlay any user edits from draftToken. The backend ignores booleans.
    const tokenUpdate: any = { ...token };
    for (const [key, val] of Object.entries(draftToken)) {
      // Skip empty strings for secret fields (boolean in original token) to avoid overwriting
      if (val === "" && typeof token[key] === "boolean") continue;
      tokenUpdate[key] = val;
    }

    const hasTokenChanges = Object.keys(draftToken).some((key) => !(draftToken[key] === "" && typeof token[key] === "boolean"));
    const hasDataChanges = Object.keys(draftData).length > 0;

    onSave({
      ...(hasTokenChanges ? { token: tokenUpdate } : {}),
      ...(hasDataChanges ? { data: { ...(fms.data || {}), ...draftData } } : {})
    });
    setDraftToken({});
    setDraftData({});
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraftToken({});
    setDraftData({});
    setIsEditing(false);
  };

  const renderFields = () => {
    if (fms.type === FMSType.SiteLink) {
      const corpCode = token.corpCode || "";
      const corpUserName = token.corpUserName || "";
      const corpPassword = token.corpPassword;
      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="Lead Unit Id" value={data.leadUnitId || ""} />
            <ReadOnlyField label="Logon corporate code" value={corpCode} />
            <ReadOnlyField label="Logon username" value={corpUserName} />
            <ReadOnlyField label="Logon password" value={corpPassword} isSecret />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <Input label="Lead Unit Id" name="leadUnitId" value={data.leadUnitId} onChange={onDataChange} />

          <Input label="Logon corporate code" name="corpCode" value={draftToken.corpCode ?? corpCode} onChange={onTokenChange} />

          <Input
            label="Logon username for the call center corporate user"
            name="corpUserName"
            value={draftToken.corpUserName ?? corpUserName}
            onChange={onTokenChange}
          />

          <SecretInput
            label="Logon password for the call center corporate user"
            name="corpPassword"
            value={draftToken.corpPassword || ""}
            isSet={!!corpPassword}
            onChange={onTokenChange}
          />
        </div>
      );
    }

    if (fms.type === FMSType.StorEdge) {
      const apiAccessId = token.apiAccessId;
      const apiSecretKey = token.apiSecretKey;
      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="API Access Id" value={apiAccessId} isSecret />
            <ReadOnlyField label="API Secret Key" value={apiSecretKey} isSecret />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <SecretInput label="API Access Id" name="apiAccessId" value={draftToken.apiAccessId || ""} isSet={!!apiAccessId} onChange={onTokenChange} />
          <SecretInput
            label="API Secret Key"
            name="apiSecretKey"
            value={draftToken.apiSecretKey || ""}
            isSet={!!apiSecretKey}
            onChange={onTokenChange}
          />
        </div>
      );
    }

    if (fms.type === FMSType.Tenant) {
      const company_id = token.company_id || "";
      const base_url = draftToken.base_url ?? token.base_url ?? "";
      const app_id = token.app_id;
      const api_key = token.api_key;

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="Company Id" value={company_id} />
            <ReadOnlyField label="Base URL" value={token.base_url || ""} />
            <ReadOnlyField label="App Id" value={app_id} isSecret />
            <ReadOnlyField label="API Key" value={api_key} isSecret />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <Input label="Company Id" name="company_id" value={draftToken.company_id ?? company_id} onChange={onTokenChange} />

          <URLInput label="Base URL" name="base_url" value={base_url} onChange={onTokenChange} />

          <SecretInput label="App Id" name="app_id" value={draftToken.app_id || ""} isSet={!!app_id} onChange={onTokenChange} />
          <SecretInput label="API Key" name="api_key" value={draftToken.api_key || ""} isSet={!!api_key} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.SSM) {
      const url = draftToken.url ?? token.url ?? "";
      const strCustomerCode = token.strCustomerCode || "";
      const strCustomerPassword = token.strCustomerPassword;

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="Customer Code" value={strCustomerCode} />
            <ReadOnlyField label="Customer Password" value={strCustomerPassword} isSecret />
            <ReadOnlyField label="URL" value={token.url || ""} />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <Input label="Customer Code" name="strCustomerCode" value={draftToken.strCustomerCode ?? strCustomerCode} onChange={onTokenChange} />

          <SecretInput
            label="Customer Password"
            name="strCustomerPassword"
            value={draftToken.strCustomerPassword || ""}
            isSet={!!strCustomerPassword}
            onChange={onTokenChange}
          />

          <URLInput label="URL" name="url" value={url} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.StorageCommander) {
      const userId = token.userId || "";
      const apiKey = token.apiKey;

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="User Id" value={userId} />
            <ReadOnlyField label="API Subscription Key" value={apiKey} isSecret />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <Input label="User Id" name="userId" value={draftToken.userId ?? userId} onChange={onTokenChange} />

          <SecretInput label="API Subscription Key" name="apiKey" value={draftToken.apiKey || ""} isSet={!!apiKey} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.DoorSwap) {
      const username = token.username || "";
      const password = token.password;

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="Username" value={username} />
            <ReadOnlyField label="Password" value={password} isSecret />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <Input label="Username" name="username" value={draftToken.username ?? username} onChange={onTokenChange} />

          <SecretInput label="Password" name="password" value={draftToken.password || ""} isSet={!!password} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.SSMCloud) {
      const url = draftToken.url ?? token.url ?? "";
      const userId = token.userId || "";
      const password = token.password;

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="User Id" value={userId} />
            <ReadOnlyField label="Password" value={password} isSecret />
            <ReadOnlyField label="URL" value={token.url || ""} />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <Input label="User Id" name="userId" value={draftToken.userId ?? userId} onChange={onTokenChange} />

          <SecretInput label="Password" name="password" value={draftToken.password || ""} isSet={!!password} onChange={onTokenChange} />

          <URLInput label="URL" name="url" value={url} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.Storage360) {
      const apiKey = token.apiKey;
      const baseUrl = draftToken.baseUrl ?? token.baseUrl ?? "";

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="API Key" value={apiKey} isSecret />
            <ReadOnlyField label="Base Url" value={token.baseUrl || ""} />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <SecretInput label="API Key" name="apiKey" value={draftToken.apiKey || ""} isSet={!!apiKey} onChange={onTokenChange} />

          <URLInput label="Base Url" name="baseUrl" value={baseUrl} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.Cubby) {
      const apiKey = token.apiKey;
      const baseUrl = draftToken.baseUrl ?? token.baseUrl ?? "";
      const organizationId = token.organizationId || "";

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="API Key" value={apiKey} isSecret />
            <ReadOnlyField label="Base Url" value={token.baseUrl || ""} />
            <ReadOnlyField label="Organization Id" value={organizationId} />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <SecretInput label="API Key" name="apiKey" value={draftToken.apiKey || ""} isSet={!!apiKey} onChange={onTokenChange} />

          <URLInput label="Base Url" name="baseUrl" value={baseUrl} onChange={onTokenChange} />

          <Input label="Organization Id" name="organizationId" value={draftToken.organizationId ?? organizationId} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.QuikStor) {
      const baseUrl = draftToken.baseUrl ?? token.baseUrl ?? "";
      const apiId = token.apiId || "";
      const apiSecret = token.apiSecret;
      const companyId = token.companyId || "";

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="Base Url" value={token.baseUrl || ""} />
            <ReadOnlyField label="Client Id" value={apiId} />
            <ReadOnlyField label="Client Secret" value={apiSecret} isSecret />
            <ReadOnlyField label="Company Id" value={companyId} />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <URLInput label="Base Url" name="baseUrl" value={baseUrl} onChange={onTokenChange} />

          <Input label="Client Id" name="apiId" value={draftToken.apiId ?? apiId} onChange={onTokenChange} />

          <SecretInput label="Client Secret" name="apiSecret" value={draftToken.apiSecret || ""} isSet={!!apiSecret} onChange={onTokenChange} />

          <Input label="Company Id" name="companyId" value={draftToken.companyId ?? companyId} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.Monument) {
      const apiKey = token.apiKey;
      const portfolioName = token.portfolioName || "";
      const baseUrl = draftToken.baseUrl ?? token.baseUrl ?? "";

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="API Key" value={apiKey} isSecret />
            <ReadOnlyField label="Portfolio Name" value={portfolioName} />
            <ReadOnlyField label="Base Url" value={token.baseUrl || ""} />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <SecretInput label="API Key" name="apiKey" value={draftToken.apiKey || ""} isSet={!!apiKey} onChange={onTokenChange} />

          <Input label="Portfolio Name" name="portfolioName" value={draftToken.portfolioName ?? portfolioName} onChange={onTokenChange} />

          <URLInput label="Base Url" name="baseUrl" value={baseUrl} onChange={onTokenChange} />
        </div>
      );
    }

    if (fms.type === FMSType.Yardi) {
      const serviceUrl = draftToken.serviceUrl ?? token.serviceUrl ?? "";
      const userName = token.userName || "";
      const password = token.password;
      const serverName = token.serverName || "";
      const database = token.database || "";
      const platform = token.platform || "";
      const interfaceEntity = token.interfaceEntity || "";
      const interfaceLicense = token.interfaceLicense;

      if (!isEditing) {
        return (
          <>
            <ReadOnlyField label="Service URL" value={token.serviceUrl || ""} />
            <ReadOnlyField label="User Name" value={userName} />
            <ReadOnlyField label="Password" value={password} isSecret />
            <ReadOnlyField label="Server Name" value={serverName} />
            <ReadOnlyField label="Database" value={database} />
            <ReadOnlyField label="Platform" value={platform} />
            <ReadOnlyField label="Interface Entity" value={interfaceEntity} />
            <ReadOnlyField label="Interface License" value={interfaceLicense} isSecret />
          </>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <URLInput label="Service URL" name="serviceUrl" value={serviceUrl} onChange={onTokenChange} />

          <Input label="User Name" name="userName" value={draftToken.userName ?? userName} onChange={onTokenChange} />

          <SecretInput label="Password" name="password" value={draftToken.password || ""} isSet={!!password} onChange={onTokenChange} />

          <Input label="Server Name" name="serverName" value={draftToken.serverName ?? serverName} onChange={onTokenChange} />

          <Input label="Database" name="database" value={draftToken.database ?? database} onChange={onTokenChange} />

          <Input label="Platform" name="platform" value={draftToken.platform ?? platform} onChange={onTokenChange} />

          <Input label="Interface Entity" name="interfaceEntity" value={draftToken.interfaceEntity ?? interfaceEntity} onChange={onTokenChange} />

          <SecretInput
            label="Interface License"
            name="interfaceLicense"
            value={draftToken.interfaceLicense || ""}
            isSet={!!interfaceLicense}
            onChange={onTokenChange}
          />
        </div>
      );
    }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <h2>API Credentials</h2>
        {isEditing ? (
          <div className="flex gap-2">
            <Button color={ButtonColor.WHITE} onClick={handleCancel}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} onClick={handleDone} disabled={hasInvalidURL}>
              Done
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button color={ButtonColor.WHITE} onClick={() => setIsEditing(true)}>
              Edit <BiEdit />
            </Button>
            <Button color={ButtonColor.RED} onClick={onRemove}>
              Remove Instance
            </Button>
          </div>
        )}
      </div>
      Use these to connect to the {fms.type} API.
      <div className="apiValues">{renderFields()}</div>
    </>
  );
};
